import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { StaffUser } from '../types';
import {
  Users,
  UserPlus,
  Shield,
  UserCheck,
  ChefHat,
  Utensils,
  KeyRound,
  Mail,
  Phone,
  Lock,
  Trash2,
  Edit2,
  Check,
  X,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  Database
} from 'lucide-react';
import { playModernSuccessChime } from '../utils/audio';

export const StaffUserManagement: React.FC = () => {
  const { staffUsers, addStaffUser, updateStaffUser, deleteStaffUser, activeStaffUser } = useApp();
  const { t } = useLanguage();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<StaffUser | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'kitchen' | 'waiter'>('kitchen');
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [showPins, setShowPins] = useState<{ [key: string]: boolean }>({});
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const togglePinVisibility = (id: string) => {
    setShowPins(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenAddUser = () => {
    setEditingUser(null);
    setName('');
    setEmail('');
    setMobile('');
    setPassword('');
    setPinCode('');
    setRole('kitchen');
    setIsActive(true);
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const handleOpenEditUser = (user: StaffUser) => {
    setEditingUser(user);
    setName(user.name);
    setEmail(user.email || '');
    setMobile(user.mobile || '');
    setPassword(user.password || '');
    setPinCode(user.pin_code);
    setRole(user.role);
    setIsActive(user.is_active);
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMsg('Full Name is required');
      return;
    }
    if (!pinCode.trim() || pinCode.trim().length !== 4 || !/^\d{4}$/.test(pinCode.trim())) {
      setErrorMsg('PIN must be exactly 4 numeric digits (e.g. 1234)');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      if (editingUser) {
        await updateStaffUser(editingUser.id, {
          name: name.trim(),
          email: email.trim() || null,
          mobile: mobile.trim() || null,
          password: password || null,
          pin_code: pinCode.trim(),
          role,
          is_active: isActive
        });
      } else {
        await addStaffUser({
          name: name.trim(),
          email: email.trim() || null,
          mobile: mobile.trim() || null,
          password: password || null,
          pin_code: pinCode.trim(),
          role,
          is_active: isActive
        });
      }
      playModernSuccessChime();
      setIsModalOpen(false);
    } catch (err: any) {
      setErrorMsg('Failed to save staff user: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (user: StaffUser) => {
    if (user.id === 'usr_admin_master') {
      alert('The Master Admin account cannot be deleted.');
      return;
    }
    if (window.confirm(`Are you sure you want to remove staff member ${user.name}?`)) {
      await deleteStaffUser(user.id);
      playModernSuccessChime();
    }
  };

  const handleToggleActive = async (user: StaffUser) => {
    if (user.id === 'usr_admin_master') {
      alert('The Master Admin account must remain active.');
      return;
    }
    await updateStaffUser(user.id, { is_active: !user.is_active });
    playModernSuccessChime();
  };

  const getRoleBadge = (r: string) => {
    switch (r) {
      case 'admin':
        return { label: 'Administrator', icon: <Shield size={13} />, bg: '#b91c1c', text: '#fff' };
      case 'manager':
        return { label: 'Manager', icon: <UserCheck size={13} />, bg: 'var(--accent-copper)', text: '#fff' };
      case 'kitchen':
        return { label: 'Kitchen / Chef', icon: <ChefHat size={13} />, bg: '#059669', text: '#fff' };
      case 'waiter':
        return { label: 'Floor Waiter', icon: <Utensils size={13} />, bg: '#2563EB', text: '#fff' };
      default:
        return { label: r, icon: <Users size={13} />, bg: 'var(--bg-surface)', text: 'var(--text-secondary)' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '10px 0' }}>
      {/* Header & Add User */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={24} color="var(--accent-copper)" />
            <span>Staff & User Access Management</span>
          </h2>
          <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Database size={14} color="var(--accent-emerald)" />
            <span>Stored securely in Cloudflare D1 Relational Database ({staffUsers.length} Users)</span>
          </p>
        </div>

        <button className="btn-primary" onClick={handleOpenAddUser} style={{ padding: '10px 18px', gap: '8px' }}>
          <UserPlus size={16} />
          <span>Add New Staff User</span>
        </button>
      </div>

      {/* Database Security Pill */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'rgba(5, 150, 105, 0.15)',
              color: 'var(--accent-emerald)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Shield size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.86rem', color: 'var(--text-primary)' }}>
              Cloudflare Edge D1 Role-Based Access Control (RBAC)
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Staff members can sign in using their unique 4-digit PIN for tablets or full Email/Password on admin terminals.
            </div>
          </div>
        </div>
      </div>

      {/* Staff Users Cards / Table */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        {staffUsers.map(user => {
          const roleBadge = getRoleBadge(user.role);
          const isMasterAdmin = user.id === 'usr_admin_master';
          const isPinVisible = showPins[user.id];

          return (
            <div
              key={user.id}
              style={{
                background: 'var(--bg-secondary)',
                border: `1px solid ${user.is_active ? 'var(--border-medium)' : 'var(--border-subtle)'}`,
                borderRadius: 'var(--radius-xl)',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                opacity: user.is_active ? 1 : 0.6,
                position: 'relative',
                boxShadow: '0 4px 14px rgba(0,0,0,0.12)'
              }}
            >
              {/* Top Row: Role Badge & Active Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span
                  style={{
                    background: roleBadge.bg,
                    color: roleBadge.text,
                    padding: '3px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.74rem',
                    fontWeight: 800,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  {roleBadge.icon}
                  <span>{roleBadge.label}</span>
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isMasterAdmin && (
                    <span
                      style={{
                        background: 'rgba(224, 90, 54, 0.15)',
                        color: 'var(--accent-copper)',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        fontSize: '0.68rem',
                        fontWeight: 800
                      }}
                    >
                      MASTER ADMIN
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleToggleActive(user)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: isMasterAdmin ? 'default' : 'pointer',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: user.is_active ? 'var(--accent-emerald)' : 'var(--text-muted)'
                    }}
                  >
                    {user.is_active ? '● Active' : '○ Inactive'}
                  </button>
                </div>
              </div>

              {/* User Name & Info */}
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
                  {user.name}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                  {user.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      <Mail size={13} color="var(--accent-copper)" />
                      <span>{user.email}</span>
                    </div>
                  )}
                  {user.mobile && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      <Phone size={13} color="var(--accent-copper)" />
                      <span>{user.mobile}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick PIN Info Bar */}
              <div
                style={{
                  background: 'var(--bg-surface)',
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '0.78rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <KeyRound size={14} color="var(--accent-copper)" />
                  <span style={{ color: 'var(--text-muted)' }}>Quick Tablet PIN:</span>
                  <strong style={{ letterSpacing: isPinVisible ? '2px' : '3px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                    {isPinVisible ? user.pin_code : '••••'}
                  </strong>
                </div>

                <button
                  type="button"
                  onClick={() => togglePinVisibility(user.id)}
                  style={{ border: 'none', background: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  title="Toggle PIN view"
                >
                  {isPinVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px', paddingTop: '10px', borderTop: '1px solid var(--border-subtle)' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ flex: 1, padding: '7px', fontSize: '0.78rem', gap: '5px' }}
                  onClick={() => handleOpenEditUser(user)}
                >
                  <Edit2 size={13} />
                  <span>Edit Profile</span>
                </button>

                {!isMasterAdmin && (
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '7px 12px', color: 'var(--accent-rose)', borderColor: 'rgba(244, 63, 94, 0.3)' }}
                    onClick={() => handleDelete(user)}
                    title="Remove user"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit User Modal */}
      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: '540px', width: '92%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserPlus size={20} color="var(--accent-copper)" />
                <span>{editingUser ? 'Edit Staff User' : 'Add New Staff User'}</span>
              </h3>
              <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            {errorMsg && (
              <div
                style={{
                  margin: '14px 20px 0 20px',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(244, 63, 94, 0.12)',
                  border: '1px solid var(--accent-rose)',
                  color: 'var(--accent-rose)',
                  fontSize: '0.78rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <AlertCircle size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={handleSaveUser}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px' }}>
                {/* 1. Full Name */}
                <div className="form-group">
                  <label className="form-label">Full Name / Henkilön Nimi *</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Mikael Virtanen"
                  />
                </div>

                {/* 2. Role Selector */}
                <div className="form-group">
                  <label className="form-label">Role & Permissions / Käyttäjärooli *</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
                    {[
                      { r: 'kitchen', label: 'Kitchen', icon: <ChefHat size={15} />, desc: 'Live KDS & prep' },
                      { r: 'waiter', label: 'Waiter', icon: <Utensils size={15} />, desc: 'Table service & POS' },
                      { r: 'manager', label: 'Manager', icon: <UserCheck size={15} />, desc: 'Dishes & orders' },
                      { r: 'admin', label: 'Admin', icon: <Shield size={15} />, desc: 'Full business control' }
                    ].map(rItem => (
                      <div
                        key={rItem.r}
                        onClick={() => setRole(rItem.r as any)}
                        style={{
                          background: role === rItem.r ? 'var(--bg-surface)' : 'var(--bg-secondary)',
                          border: `2px solid ${role === rItem.r ? 'var(--accent-copper)' : 'var(--border-subtle)'}`,
                          borderRadius: 'var(--radius-md)',
                          padding: '10px 8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ color: role === rItem.r ? 'var(--accent-copper)' : 'var(--text-secondary)' }}>
                          {rItem.icon}
                        </div>
                        <div style={{ fontWeight: 800, fontSize: '0.78rem', color: role === rItem.r ? 'var(--accent-copper)' : 'var(--text-primary)' }}>
                          {rItem.label}
                        </div>
                        <div style={{ fontSize: '0.64rem', color: 'var(--text-muted)' }}>{rItem.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3. Mobile Number & Email */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={mobile}
                      onChange={e => setMobile(e.target.value)}
                      placeholder="+358 40 123 4567"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-input"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="name@theovenstory.fi"
                    />
                  </div>
                </div>

                {/* 4. Quick PIN & Password */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Quick 4-Digit PIN *</label>
                    <input
                      type="text"
                      maxLength={4}
                      required
                      className="form-input"
                      value={pinCode}
                      onChange={e => setPinCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="e.g. 4550"
                      style={{ letterSpacing: '4px', fontWeight: 800, textAlign: 'center' }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password (Optional)</label>
                    <input
                      type="password"
                      className="form-input"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="For email login..."
                    />
                  </div>
                </div>

                {/* Active Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    id="userActiveCheck"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--accent-copper)' }}
                  />
                  <label htmlFor="userActiveCheck" style={{ fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    Account is Active (Allow login)
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingUser ? 'Update User' : 'Create Staff Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffUserManagement;
